import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import { IoCloseSharp } from 'react-icons/io5';
import * as yup from 'yup';
import { getRequiredErrorMessage } from '../../../../utils/common.utils';
import Select from '../../../form-controls/Select';
import TextInput from '../../../form-controls/TextInput';
import FileUpload from '../../../form-controls/FileUpload';
import { DropZoneComponent } from '../../../DropzoneComponent/DropzoneComponent';
import { PropsFromRedux } from '.';
import { IDType } from '../../../../interfaces/shipping.interface';
import { API_KEYS } from '../../../../interfaces/api';
import { BeatLoader } from '../../../Spinners/Spinner';
import { useOverseasFormContext } from '../../../../context/OverseasShippingFormContext/context';
import { OVERSEAS_SHIPPING_FORM_TYPES } from '../../../../context/OverseasShippingFormContext/reducer';

interface Props extends PropsFromRedux {}

const schema = yup.object().shape({
  IdentificationType: yup
    .object()
    .shape({
      Name: yup.string(),
      Value: yup.number(),
    })
    .required(getRequiredErrorMessage('Mode of Identification'))
    .typeError('Select an ID type'),
  IdentificationNumber: yup
    .string()
    .max(15, 'Invalid identification number')
    .required(getRequiredErrorMessage('Identification number')),
  IdentificationImage: yup
    .string() 
    .required(getRequiredErrorMessage('Image is required'))
    .typeError('Image is required'),
});

interface Inputs {
  IdentificationType: IDType;
  IdentificationNumber: string;
  IdentificationImage: string; 
}

const SenderIdForm: React.FC<Props> = ({
  hideModal,
  getIdTypes,
  idTypes,
  user,
  idVerification,
  verifyId,
  createShipment,
  createShipmentState,
  resetApi,
}) => {
  const userData = user?.response?.Object;

  const { handleSubmit, control, errors } = useForm<Inputs>({
    resolver: yupResolver(schema),
  });

  const { formState, dispatch } = useOverseasFormContext();

  React.useEffect(() => {
    getIdTypes();
  }, [getIdTypes]);

  const IdTypes: IDType[] = (idTypes?.response as unknown as IDType[]) || [];

  const onSubmit = (data: Inputs) => {
    console.log('Form Data:', data);

    verifyId({
      ...data,
      IdentificationType: data.IdentificationType?.Name,
    });
    dispatch({
      type: OVERSEAS_SHIPPING_FORM_TYPES.UPDATE_VALUE,
      values: {
        UserId: userData?.UserId,
      },
    });
  };

  React.useEffect(() => {
    if (!idVerification?.loading && idVerification?.success) {
      resetApi(API_KEYS.PROFILE_INTL_USER);
      if (formState.values) {
        createShipment(formState.values);
      }
    }
  }, [
    createShipment,
    formState.values,
    idVerification?.loading,
    idVerification?.success,
    resetApi,
  ]);

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="w-full px-4 bg-white rounded-md lg:px-14 py-10 relative"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={() => {
          hideModal();
          resetApi(API_KEYS.CREATE_INTL_SHIPMENT_REQUEST);
        }}
      >
        <IoCloseSharp className="sticky sm:absolute cursor-pointer text-2xl top-0 left-full sm:left-auto sm:right-5 sm:top-5" />
      </button>

      <h4 className="text-center text-2xl mb-5 font-bold">One more thing</h4>
      <div className="w-full max-w-lg mx-auto">
        <p className="text-center mb-2 font-semibold text-xl">
          The security of your item(s) and fraud prevention is important to GIG
          Logistics.
        </p>
        <p className="text-center font-medium">
          Please click on the drop down below to select and upload a form of
          identification to further guarantee the security of your item.
        </p>
      </div>
      <div className="w-full mx-auto max-w-md py-10">
        <div className="mb-8 w-full">
          <Controller
            name="IdentificationType"
            control={control}
            defaultValue=""
            render={({ name, value, onChange }) => (
              <Select
                name={name}
                value={value}
                id={name}
                wrapperClassName="w-full"
                error={errors['IdentificationType']}
                placeholder="Mode of Identification"
                labelClassName="mb-2 ml-2 flex"
                labelKey="Name"
                valueKey="Value"
                customChange={onChange}
                className="rounded-lg"
                options={IdTypes}
                noOptionsMessage={() =>
                  idTypes?.loading ? 'Loading...' : 'No ID types found'
                }
              />
            )}
          />
        </div>
        <div className="mb-5 w-full">
          <Controller
            name="IdentificationNumber"
            control={control}
            defaultValue=""
            render={({ name, value, onChange }) => (
              <TextInput
                name={name}
                value={value}
                className="forminput"
                id={name}
                wrapperClassName="w-full"
                error={errors['IdentificationNumber']}
                placeholder="Identification number"
                labelClassName="mb-2 ml-2 flex"
                customChange={onChange}
              />
            )}
          />
        </div>
        <div className="w-full flex">
          <Controller
            name="IdentificationImage"
            control={control}
            defaultValue=""
            render={({ name, value, onChange }) => (
              <FileUpload
                wrapperClassName="w-full"
                labelClassName="mb-2 ml-1 flex"
                label="Upload item"
                name={name}
                id={name}
                maxFiles={1}
                value={value}
                customChange={(files: string[]) => {
                  onChange(files[0] || '');
                }}

                error={errors['IdentificationImage']}
                accept="image/*"
                DropZoneComponent={DropZoneComponent}
              />
            )}
          />
        </div>
      </div>
      {idVerification?.error && (
        <div className="mb-5 p-4 bg-red-200 text-red-500 rounded-lg font-medium">
          Error uploading your ID. Check the information you entered
        </div>
      )}

      {createShipmentState?.error && (
        <div className="mb-5 p-4 bg-red-200 text-red-500 rounded-lg font-medium">
          Error creating your shipment. Check the information you entered
        </div>
      )}

      <div className="flex justify-center w-full">
        <button
          type="submit"
          disabled={idVerification?.loading || createShipmentState?.loading}
          className="mb-5 text-white bg-secondary font-bold py-3 w-full max-w-xs rounded"
        >
          {idVerification?.loading ? (
            <>
              Uploading your ID <BeatLoader color="white" />
            </>
          ) : createShipmentState?.loading ? (
            <>
              Creating your shipment <BeatLoader color="white" />
            </>
          ) : (
            'Finish'
          )}
        </button>
      </div>
    </form>
  );
};

export default SenderIdForm;
